import { useEffect, useMemo, useState } from "react";
import Nav from "../components/site/Nav";
import Footer from "../components/site/Footer";
import CaseStudyCard from "../components/site/CaseStudyCard";
import { workAssets } from "../components/site/Portfolio";
import { useT } from "../i18n/LanguageContext";
import useReveal from "../hooks/useReveal";
import { track } from "../lib/analytics";

const SITE_URL = "https://gfirem.com";

// Collect the unique industry & product-type keys actually in use by workAssets,
// preserving insertion order so filter chips stay stable as we add projects.
function uniqueOrdered(items) {
    const seen = new Set();
    const out = [];
    for (const v of items) {
        if (!seen.has(v)) {
            seen.add(v);
            out.push(v);
        }
    }
    return out;
}

function useIndexHead(lang, metaTitle, metaDescription) {
    useEffect(() => {
        const canonical = `${SITE_URL}/case-studies`;
        const ogImage = `${SITE_URL}/og/case-studies-index.jpg`;
        const snapshot = {
            title: document.title,
            description:
                document.head
                    .querySelector('meta[name="description"]')
                    ?.getAttribute("content") ?? null,
            canonical:
                document.head
                    .querySelector('link[rel="canonical"]')
                    ?.getAttribute("href") ?? null,
            ogImage:
                document.head
                    .querySelector('meta[property="og:image"]')
                    ?.getAttribute("content") ?? null,
            twImage:
                document.head
                    .querySelector('meta[name="twitter:image"]')
                    ?.getAttribute("content") ?? null,
        };

        document.title = metaTitle;
        document.documentElement.lang = lang === "es" ? "es" : "en";

        const upsert = (selector, create) => {
            let el = document.head.querySelector(selector);
            if (!el) {
                el = create();
                document.head.appendChild(el);
            }
            return el;
        };

        upsert('meta[name="description"]', () => {
            const m = document.createElement("meta");
            m.name = "description";
            return m;
        }).setAttribute("content", metaDescription);

        upsert('link[rel="canonical"]', () => {
            const l = document.createElement("link");
            l.rel = "canonical";
            return l;
        }).setAttribute("href", canonical);

        upsert('meta[property="og:image"]', () => {
            const m = document.createElement("meta");
            m.setAttribute("property", "og:image");
            return m;
        }).setAttribute("content", ogImage);

        upsert('meta[name="twitter:image"]', () => {
            const m = document.createElement("meta");
            m.name = "twitter:image";
            return m;
        }).setAttribute("content", ogImage);

        // CollectionPage + BreadcrumbList JSON-LD — helps Google + AI answer
        // engines understand the /case-studies index as a listing and show
        // "Home › Case studies" in SERPs.
        const scriptId = "case-studies-index-jsonld";
        let script = document.getElementById(scriptId);
        if (!script) {
            script = document.createElement("script");
            script.type = "application/ld+json";
            script.id = scriptId;
            document.head.appendChild(script);
        }
        script.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "CollectionPage",
                    "@id": `${canonical}#collection`,
                    name: metaTitle,
                    description: metaDescription,
                    url: canonical,
                    inLanguage: lang === "es" ? "es-LA" : "en-US",
                    primaryImageOfPage: ogImage,
                    hasPart: workAssets.map((a, i) => ({
                        "@type": "CreativeWork",
                        position: i + 1,
                        url: `${canonical}/${a.slug}`,
                        name: a.slug,
                    })),
                },
                {
                    "@type": "BreadcrumbList",
                    "@id": `${canonical}#breadcrumbs`,
                    itemListElement: [
                        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
                        { "@type": "ListItem", position: 2, name: "Case studies", item: canonical },
                    ],
                },
            ],
        });

        return () => {
            document.title = snapshot.title;
            if (snapshot.description !== null) {
                document.head
                    .querySelector('meta[name="description"]')
                    ?.setAttribute("content", snapshot.description);
            }
            if (snapshot.canonical !== null) {
                document.head
                    .querySelector('link[rel="canonical"]')
                    ?.setAttribute("href", snapshot.canonical);
            }
            if (snapshot.ogImage !== null) {
                document.head
                    .querySelector('meta[property="og:image"]')
                    ?.setAttribute("content", snapshot.ogImage);
            }
            if (snapshot.twImage !== null) {
                document.head
                    .querySelector('meta[name="twitter:image"]')
                    ?.setAttribute("content", snapshot.twImage);
            }
            const s = document.getElementById(scriptId);
            if (s && s.parentNode) s.parentNode.removeChild(s);
        };
    }, [lang, metaTitle, metaDescription]);
}

export default function CaseStudiesIndex() {
    useReveal();
    const { t, lang } = useT();
    const idx = t("portfolio.index");
    const filters = t("portfolio.filters");
    const copies = t("portfolio.works");

    useIndexHead(lang, idx.metaTitle, idx.metaDescription);

    const industries = useMemo(
        () => uniqueOrdered(workAssets.map((a) => a.industry)),
        []
    );
    const productTypes = useMemo(
        () => uniqueOrdered(workAssets.flatMap((a) => a.productTypes || [])),
        []
    );
    const outcomes = useMemo(
        () => uniqueOrdered(workAssets.flatMap((a) => a.outcomes || [])),
        []
    );

    const [activeIndustry, setActiveIndustry] = useState("all");
    const [activeType, setActiveType] = useState("all");
    const [activeOutcome, setActiveOutcome] = useState("all");

    // Wrap the setters so every filter click emits a semantic event before state updates.
    // "all" is treated as a clear, so we send value="all" and let PostHog segment.
    const changeIndustry = (v) => {
        if (v === activeIndustry) return;
        track("filter_changed", { facet: "industry", value: v, from: activeIndustry });
        setActiveIndustry(v);
    };
    const changeType = (v) => {
        if (v === activeType) return;
        track("filter_changed", { facet: "product_type", value: v, from: activeType });
        setActiveType(v);
    };
    const changeOutcome = (v) => {
        if (v === activeOutcome) return;
        track("filter_changed", { facet: "outcome", value: v, from: activeOutcome });
        setActiveOutcome(v);
    };

    const items = useMemo(() => {
        return workAssets
            .map((a, i) => ({ asset: a, copy: copies[i], i }))
            .filter(({ asset }) => {
                if (activeIndustry !== "all" && asset.industry !== activeIndustry)
                    return false;
                if (
                    activeType !== "all" &&
                    !(asset.productTypes || []).includes(activeType)
                )
                    return false;
                if (
                    activeOutcome !== "all" &&
                    !(asset.outcomes || []).includes(activeOutcome)
                )
                    return false;
                return true;
            });
    }, [copies, activeIndustry, activeType, activeOutcome]);

    const anyActive =
        activeIndustry !== "all" ||
        activeType !== "all" ||
        activeOutcome !== "all";
    const resultsLabel =
        items.length === 1 ? filters.resultsOne : filters.resultsMany;

    return (
        <main data-testid="case-studies-index-page" data-cta-source="case_studies_index" className="bg-sand text-ink">
            <div className="fixed top-0 left-0 right-0 z-50">
                <Nav />
            </div>

            <section className="pt-28 md:pt-36 pb-20 md:pb-28 border-b border-warm">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-14 md:mb-20">
                        <div className="lg:col-span-8 reveal">
                            <p className="overline mb-5">{idx.overline}</p>
                            <h1 className="font-serif text-4xl sm:text-5xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-ink">
                                {idx.headlineA}{" "}
                                <em className="not-italic text-terracotta">
                                    {idx.headlineEm}
                                </em>
                                {idx.headlineB ? ` ${idx.headlineB}` : ""}
                            </h1>
                            <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-secondary">
                                {idx.intro}
                            </p>
                        </div>
                        <div className="lg:col-span-4 lg:pl-6 reveal flex lg:justify-end lg:items-end">
                            <div className="border-t border-ink pt-4 w-full">
                                <div className="font-mono text-xs uppercase tracking-[0.2em] text-secondary">
                                    <span data-testid="case-studies-count">
                                        {String(items.length).padStart(2, "0")}
                                    </span>{" "}
                                    / {String(workAssets.length).padStart(2, "0")}{" "}
                                    {resultsLabel}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="reveal grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 mb-14">
                        <FilterGroup
                            testid="filter-outcome"
                            title={filters.outcomeTitle}
                            options={outcomes}
                            active={activeOutcome}
                            onChange={changeOutcome}
                            allLabel={filters.allLabel}
                            labelsForKey={(k) => filters.outcomes[k] || k}
                        />
                        <FilterGroup
                            testid="filter-industry"
                            title={filters.industryTitle}
                            options={industries}
                            active={activeIndustry}
                            onChange={changeIndustry}
                            allLabel={filters.allLabel}
                            labelsForKey={(k) => filters.industries[k] || k}
                        />
                        <FilterGroup
                            testid="filter-product-type"
                            title={filters.productTypeTitle}
                            options={productTypes}
                            active={activeType}
                            onChange={changeType}
                            allLabel={filters.allLabel}
                            labelsForKey={(k) => filters.productTypes[k] || k}
                        />
                    </div>

                    {anyActive && (
                        <div className="mb-10 reveal">
                            <button
                                type="button"
                                data-testid="filter-reset"
                                onClick={() => {
                                    track("filter_reset", {
                                        from_industry: activeIndustry,
                                        from_product_type: activeType,
                                        from_outcome: activeOutcome,
                                    });
                                    setActiveIndustry("all");
                                    setActiveType("all");
                                    setActiveOutcome("all");
                                }}
                                className="text-xs uppercase tracking-[0.18em] font-mono text-secondary hover:text-ink transition-colors underline underline-offset-4"
                            >
                                ← {filters.reset}
                            </button>
                        </div>
                    )}

                    {items.length > 0 ? (
                        <div
                            data-testid="case-studies-grid"
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
                        >
                            {items.map(({ asset, copy, i }) => (
                                <div key={asset.slug} className="reveal">
                                    <CaseStudyCard
                                        asset={asset}
                                        copy={copy}
                                        index={i}
                                        testid={`case-studies-card-${asset.slug}`}
                                        source="index"
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div
                            data-testid="case-studies-empty"
                            className="reveal border border-warm p-10 text-center text-secondary font-serif text-lg"
                        >
                            {filters.empty}
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </main>
    );
}

const FilterGroup = ({
    title,
    options,
    active,
    onChange,
    allLabel,
    labelsForKey,
    testid,
}) => (
    <div data-testid={testid}>
        <p className="overline mb-4">{title}</p>
        <div className="flex flex-wrap gap-2">
            <FilterChip
                testid={`${testid}-all`}
                active={active === "all"}
                onClick={() => onChange("all")}
            >
                {allLabel}
            </FilterChip>
            {options.map((k) => (
                <FilterChip
                    key={k}
                    testid={`${testid}-${k}`}
                    active={active === k}
                    onClick={() => onChange(k)}
                >
                    {labelsForKey(k)}
                </FilterChip>
            ))}
        </div>
    </div>
);

const FilterChip = ({ active, onClick, children, testid }) => (
    <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        aria-pressed={active}
        className={`text-xs font-mono uppercase tracking-[0.14em] px-4 py-2 border transition-colors ${
            active
                ? "bg-ink text-sand border-ink"
                : "bg-transparent text-ink/80 border-warm hover:border-ink"
        }`}
    >
        {children}
    </button>
);
